import { useCallback, useEffect, useState } from 'react';
import {
  SavedLocation,
  SavedLocationSourceType,
  SaveLocationInput,
  fetchSavedLocations,
  createSavedLocation,
  updateSavedLocation,
  deleteSavedLocation,
} from '../lib/api';

export interface UseSavedLocations {
  locations: SavedLocation[];
  loading: boolean;
  error: string | null;
  save: (input: SaveLocationInput) => Promise<SavedLocation>;
  rename: (id: number, name: string) => Promise<SavedLocation>;
  remove: (id: number) => Promise<void>;
  findBySource: (
    source_type: SavedLocationSourceType,
    source_id: string
  ) => SavedLocation | undefined;
}

export function useSavedLocations(): UseSavedLocations {
  const [locations, setLocations] = useState<SavedLocation[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchSavedLocations()
      .then(list => { if (!cancelled) setLocations(list); })
      .catch(e => {
        if (cancelled) return;
        const msg = e instanceof Error ? e.message : 'Failed to load saved locations';
        setError(msg);
        console.error('[useSavedLocations] fetch error:', msg);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const save = useCallback(async (input: SaveLocationInput) => {
    const created = await createSavedLocation(input);
    setLocations(prev => [created, ...prev]);
    return created;
  }, []);

  const rename = useCallback(async (id: number, name: string) => {
    const updated = await updateSavedLocation(id, { name });
    setLocations(prev => prev.map(l => (l.id === id ? updated : l)));
    return updated;
  }, []);

  const remove = useCallback(async (id: number) => {
    await deleteSavedLocation(id);
    setLocations(prev => prev.filter(l => l.id !== id));
  }, []);

  const findBySource = useCallback(
    (source_type: SavedLocationSourceType, source_id: string) => {
      return locations.find(
        l => l.source_type === source_type && l.source_id === source_id
      );
    },
    [locations]
  );

  return { locations, loading, error, save, rename, remove, findBySource };
}
